import videojs from 'video.js'
import { RewindTypes, SEEK_ANIMATION } from '|>/shared/constants/rewind'
import { Events } from '|>/shared/events'
import { bind_, throttle, UPDATE_REFRESH_INTERVAL } from '|>/shared/fn'
import { findPosition, getPointerPosition } from '|>/shared/h'
import { Intent } from '|>/shared/types/intent'
import { on, register } from '|>/shared/vjs'
import { BaseLoadProgressBar, BaseSlider } from '../base'
import { LivePlayProgressBar } from './live-play-progress-bar'
import { MouseBar } from './mouse-bar'
import { LivePauseControl } from './pause-control'

import './live-seek-bar.css'

const SEEK_COOLDOWN_MS = 500

const MANY_CLICKS_WAIT_MS = 400

const CHECK_NEW_URL_READY_INTERVAL = 300

const PROGRESS_BAR_LIVE_TOLERANCE_PIXELS = 7

// Options of progress calculation
type TOptions = { paused?: boolean }

/**
 * Live seekable bar
 */
@register
export class LiveSeekBar extends BaseSlider {
  declare playerEvent: string
  declare livePlayProgressBar: LivePlayProgressBar
  declare mouseBar: MouseBar

  @on(Events.Media.IsRewindPending)
  private isRewindPending: Events.Media.IsRewindPending = false

  @on(Events.Media.IsRewound)
  private isRewound: Events.Media.IsRewound = false

  @on(Events.Media.RewindDetails)
  private rewindDetails: Events.Media.RewindDetails

  // 'pause live feature' paused on - latest stamp
  @on(Events.Player.DelayedLiveRewind)
  private pauseMomentTimestampSec: Events.Player.DelayedLiveRewind = 0

  updateInterval: number | undefined
  isOn: boolean = false // state, if boundaries should be calculated by interval

  tempPercent: number | undefined // [0 - 1]
  start: number | undefined // stamp ms
  end: number | undefined // stamp ms

  constructor(player) {
    super(player)

    /* Handlers from this component is used by reference by rewind buttons and hotkeys */
    ;(player as any).liveSeekBar = this

    this.updateOwnPositionForced = bind_(this, this.updateOwnPosition)
    this.updateOwnPosition = throttle(
      this.updateOwnPositionForced,
      UPDATE_REFRESH_INTERVAL
    )

    this.updateForced = bind_(this, this.update)
    this.update = throttle(this.updateForced, UPDATE_REFRESH_INTERVAL)

    this.handleKeyDown = bind_(this, this.handleKeyDown)
    videojs.on(document, 'keydown', this.handleKeyDown)
  }

  override createEl() {
    return super.createEl(
      'div',
      { className: 'vjs-progress-holder vjs-x-live-progress-holder' },
      { 'aria-label': this.localize('Live Progress Bar') }
    )
  }

  getLivePercent() {
    // atm we could go rewind only by user interaction -> new url
    // so any live playback network issues should not cause live edge be lost
    return 1
  }

  getLiveStamp(): number | undefined {
    return Date.now()
  }

  getRewoundStamp(options?: TOptions): number | undefined {
    if (
      this.end === undefined ||
      this.start === undefined ||
      !this.rewindDetails
    )
      return

    const { paused = this.player_?.paused() } = options ?? {}

    const { rewoundTargetStartSec, rewoundUrlStartSec, rewindType } =
      this.rewindDetails

    const currentTime = this.player_.currentTime()
    const pauseMomentTimestamp = (this.pauseMomentTimestampSec ?? 0) * 1000

    const isLivePaused = Boolean(paused && pauseMomentTimestamp)

    if (rewindType === RewindTypes.timeshift) {
      if (isLivePaused) {
        // this allows to show progress stops when in pause
        return pauseMomentTimestamp
      } else {
        // playing
        return (rewoundTargetStartSec + currentTime) * 1000
      }
    } else {
      // that "dvr/rewind" case
      if (isLivePaused) {
        return pauseMomentTimestamp
      } else {
        // playing
        return (rewoundUrlStartSec + currentTime) * 1000
      }
    }
  }

  getRewoundPercent(options?: TOptions) {
    const currentStampMs = this.getRewoundStamp(options)

    if (!currentStampMs || !this.end || !this.start) return

    const progressLengthMs = this.end - this.start

    return (currentStampMs - this.start) / progressLengthMs
  }

  getPercent() {
    const isTempPercent =
      this.tempPercent != null && this.tempPercent >= 0 && this.tempPercent <= 1

    if (isTempPercent || this.isRewindPending || this.totalShift) {
      return this.tempPercent
    }

    // player have nothing
    if (this.player_.readyState() === 0) {
      return 0
    }

    return this.getPlaybackPercent()
  }

  getPlaybackPercent(options?: TOptions) {
    if (this.isRewound) return this.getRewoundPercent(options)

    return this.getLivePercent()
  }

  getPlaybackStamp(options?: TOptions) {
    if (this.isRewound) return this.getRewoundStamp(options)

    return this.getLiveStamp()
  }

  override handleMouseMove(event, _mouseDown = false) {
    if (event) {
      event.stopPropagation()
    }

    if (!this.parentComponent_.isInteractive) return

    if (this.mouseBar && this.livePlayProgressBar.mouseTimeDisplay) {
      const seekBarRect = findPosition(this.el_)
      const point = getPointerPosition(this.el_, event).x
      const pointPx = point * seekBarRect.width

      const seekBarRectShifted = this.parentComponent_.getSeekBarRectShifted()

      // Update mouseBar width
      this.mouseBar.update(seekBarRect, point)
      // Update mouseTimeDisplay position
      this.livePlayProgressBar.mouseTimeDisplay.updateTooltip(
        seekBarRectShifted,
        point,
        pointPx,
        this.parentComponent_.seekStartMax!,
        this.parentComponent_.seekEndMin!
      )
    }

    super.handleMouseMove(event)

    this.tempPercent = this.calculateDistance(event)
  }

  /*
   * Last progress bar click may trigger new url load and player init new sourceUrl
   * so we check isRewindPending, totalShift and keeping saved last click point in tempPercent
   */
  clearWithDelayTempPercent(trailing: boolean = false) {
    if (trailing)
      return window.setTimeout(
        bind_(this, this.clearWithDelayTempPercent),
        CHECK_NEW_URL_READY_INTERVAL
      )

    if (!this.isRewindPending && !this.totalShift && !this.player_?.paused())
      return (this.tempPercent = undefined)

    window.setTimeout(
      bind_(this, this.clearWithDelayTempPercent),
      CHECK_NEW_URL_READY_INTERVAL
    )
  }

  @on(Events.Player.GoLive)
  setTempPercentOnGoLive() {
    this.tempPercent = this.getPercent()
    // delay is to wait for isRewindPending become true
    this.clearWithDelayTempPercent(true)
  }

  override handleMouseDown(event) {
    if (event) {
      event.stopPropagation()
      // this prevents secondary click events on touch devices
      event.preventDefault()
    }

    const parentEl = this.parentComponent_.el()
    if (parentEl) {
      parentEl.classList.add('vjs-hover-active')
    }

    super.handleMouseDown(event)
  }

  totalShift: number = 0
  private accumulateTimeout: ReturnType<typeof setTimeout> | null = null
  private seekCooldownTimerId: ReturnType<typeof setTimeout> | null = null

  isLivePercent(percent?: number) {
    if (!percent) return false

    const elWidth = this.el_.offsetWidth
    const percentPerPixel = 1 / elWidth // one pixel presents such percent

    const liveTolerancePercent =
      percentPerPixel * PROGRESS_BAR_LIVE_TOLERANCE_PIXELS

    return (
      percent + liveTolerancePercent >= 1 &&
      elWidth >= PROGRESS_BAR_LIVE_TOLERANCE_PIXELS * 2
    )
  }

  isMinPercent(percent?: number) {
    if (!percent) return false

    return percent < 0
  }

  getNewPosition(params):
    | {
        percent: number
        stamp: number
      }
    | undefined {
    /* shiftSec - negative for left and positive for the right */
    if (!this.start || !this.end) return

    const { event, shiftSec } = params

    const progressLengthMs = this.end - this.start

    let percent
    let stamp

    if (event) {
      // this is the new playback percent, not normalized yet, that would be shown while loading
      percent = this.calculateDistance(event)

      if (!percent) return

      stamp = Math.round((this.start + progressLengthMs * percent) / 1000)
    }

    if (shiftSec) {
      const currentPercent = this.getPlaybackPercent()
      if (currentPercent == null) return

      percent =
        currentPercent +
        ((this.totalShift ?? shiftSec) * 1000) / progressLengthMs

      // stamp not needed for shift seek
    }

    return { percent, stamp }
  }

  trySeek(params: { event?: Event; shiftSec?: number; animation?: string }) {
    if (this.seekCooldownTimerId) return

    if (!this.parentComponent_.isInteractive) return

    if (!this.end || !this.start) return

    const { shiftSec, animation } = params

    const isShiftingBackwards = shiftSec && shiftSec < 0

    const { percent, stamp } = this.getNewPosition(params) ?? {}

    if (animation === SEEK_ANIMATION.TAP)
      this.player_.tapHandler.animateSeek(shiftSec)
    if (animation === SEEK_ANIMATION.CLICK)
      this.player_.arrowSeekHandlers.animateSeek(shiftSec)

    this.tempPercent = percent
    this.clearWithDelayTempPercent(true) // delay clearing tempPercent, to show last user input

    /* Right border collision handling */
    if (this.isLivePercent(percent) && !isShiftingBackwards)
      return this.goLive()

    /* Left border collision handling */
    if (this.isMinPercent(percent) && isShiftingBackwards)
      return this.debouncedSeek(this.start / 1000)

    if (stamp) return this.debouncedSeek(stamp)

    if (shiftSec) {
      this.update() // updates playback position if seeking while in pause
      return this.throttledSeek(shiftSec)
    }
  }

  private debouncedSeek(rewindParam?: number) {
    if (this.seekCooldownTimerId) return

    this.doSeek(rewindParam)

    this.seekCooldownTimerId = setTimeout(() => {
      this.totalShift = 0
      this.seekCooldownTimerId = null
    }, SEEK_COOLDOWN_MS)
  }

  seekForce(timeStamp?: number) {
    this.debouncedSeek(timeStamp)
  }

  private throttledSeek(shift: number) {
    this.totalShift += shift

    // Reset seek call timeout if user clicks again within MANY_CLICKS_WAIT_MS
    if (this.accumulateTimeout) clearTimeout(this.accumulateTimeout)

    this.accumulateTimeout = setTimeout(() => {
      this.debouncedSeek(this.totalShift)
    }, MANY_CLICKS_WAIT_MS)
  }

  doSeek(value) {
    this.player_.trigger(Events.User.Intent, Intent.Rewind)
    this.player_.trigger(Events.Player.LiveRewind, value)
  }

  goLive() {
    if (this.isRewound) {
      this.player_.trigger(Events.User.Intent, Intent.GoLive)
      this.player_.trigger(Events.Player.GoLive)

      this.totalShift = 0

      this.seekCooldownTimerId = setTimeout(() => {
        this.seekCooldownTimerId = null
      }, SEEK_COOLDOWN_MS)
    }
  }

  override handleMouseUp(event) {
    const parentEl = this.parentComponent_.el()
    if (parentEl) {
      parentEl.classList.remove('vjs-hover-active')
    }

    if (event) {
      event.stopPropagation()
      // this prevents secondary click events on touch devices
      event.preventDefault()
    }

    super.handleMouseUp(event)

    this.trySeek({ event })
  }

  override handleKeyDown(_event) {
    // console.log('👉', event.key)
  }

  updateForced: typeof this.update
  override update() {
    return super.update()
    // this.updateOwnPosition(width, boundaries)
    // const percent = this.getPercent()
    // this.bar.update(percent)
  }

  /**
   * (------[======]--------)
   * a      x      y        b
   */
  updateOwnPositionForced: typeof this.updateOwnPosition
  updateOwnPosition(
    width: number,
    [a, x, y, b]: [number, number, number, number]
  ) {
    if (!this.isOn) return

    const w = width / (b - a)
    const left = x === a ? 0 : Math.max(w * (x - a), 0)
    const right = y === b ? 0 : Math.max(w * (b - y), 0)

    const el = this.el() as HTMLElement
    el.style['margin-left'] = `${left}px`
    el.style['margin-right'] = `${right}px`

    // update start and end points
    if (x !== 0 && y !== 1) {
      this.start = x
      this.end = y
    }
  }

  @on('playing')
  enableInterval() {
    if (this.updateInterval) {
      return
    }

    this.updateInterval = this.setInterval(this.update, UPDATE_REFRESH_INTERVAL)
  }

  @on(['ended', 'pause', 'waiting', 'dispose', 'playerreset'])
  disableInterval() {
    if (!this.updateInterval) {
      return
    }

    this.clearInterval(this.updateInterval)
    this.updateInterval = undefined
  }

  override dispose() {
    videojs.off(document, 'keydown', this.handleKeyDown)
    super.dispose()
  }

  reset() {
    this.start = undefined
    this.end = undefined
    this.livePlayProgressBar.el_.style['width'] = '0px'
    this.updateOwnPositionForced(0, [0, 0, 0, 0])
    this.updateForced()
  }

  resetStyles() {
    const el = this.el() as HTMLElement
    el.style['margin-left'] = 'unset'
    el.style['margin-right'] = 'unset'
  }

  @on('loadedmetadata')
  startBoundariesCalculations() {
    // useless calculations, untill metadata successfully loaded
    this.isOn = true
  }

  @on('playerreset')
  onReset() {
    /*
     * Stop boundaries calculations,
     * next content may have correct boundaries, but not allowed to play.
     */
    this.isOn = false
    this.resetStyles()
  }
}
LiveSeekBar.prototype.playerEvent = 'mousemove'

LiveSeekBar.options = {
  children: [
    LivePlayProgressBar.as('livePlayProgressBar'),
    MouseBar.as('mouseBar'),
    BaseLoadProgressBar.as('loadProgressBar'),
    // handle pause in live content
    LivePauseControl,
  ],
  barName: 'livePlayProgressBar',
}
